import type { BrandingPayload } from "@gatiq/shared";

/**
 * The whole client-side branding engine.
 *
 * The server has already merged the org's overrides onto the platform
 * defaults and computed every derived token, so this function does no
 * decision-making — it writes CSS variables, the tab title, the favicon and
 * the font stylesheet, and the entire interface repaints.
 */
export function applyBranding(payload: BrandingPayload): void {
  const root = document.documentElement;

  for (const [name, value] of Object.entries(payload.cssVars)) {
    root.style.setProperty(name, value);
  }

  const appName = payload.branding.appName;
  if (appName) document.title = appName;

  const favicon = payload.branding.favicon;
  if (favicon) {
    const link = document.getElementById("app-favicon") as HTMLLinkElement | null;
    if (link) link.href = favicon;
  }

  // Only touch the font stylesheet when this tenant actually uses other faces,
  // so the default GatiQ theme never re-downloads Poppins and Outfit.
  const fontsLink = document.getElementById("app-fonts") as HTMLLinkElement | null;
  if (fontsLink && payload.fontsUrl && fontsLink.href !== payload.fontsUrl) {
    fontsLink.href = payload.fontsUrl;
  }

  const themeColor = payload.branding.color1;
  if (themeColor) {
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", themeColor);
  }

  // Update the PWA manifest so the installed icon and name match the org.
  //
  // The manifest must be rewritten per tenant, but it has to stay a URL the
  // browser will actually fetch and parse. A `data:` URL looks equivalent and
  // is not: Chrome ignores a data-URL manifest for installability, so the
  // "install app" offer never appears. A Blob URL is a real, same-origin,
  // fetchable manifest — Chrome parses it and honours it.
  //
  // But a Blob URL has no page to resolve against, so every relative URL in the
  // manifest — `start_url`, `scope`, every icon `src` — becomes unresolvable.
  // Chrome reports `start-url-not-valid` and `manifest-missing-suitable-icon`,
  // sees a manifest with no icons at all, and refuses to install. So the
  // manifest is absolutised before it is written out. That is correct for a
  // real fetch too, and harmless.
  //
  // The link's href is a URL, not the manifest's contents, so this fetches the
  // real file to get the object it needs to patch. The result is memoised on
  // the element so a theme change that does not touch the name still rewrites
  // without re-fetching on every paint.
  const manifest = document.getElementById("app-manifest") as HTMLLinkElement | null;
  if (manifest && appName) {
    rewriteManifest(manifest, appName, themeColor ?? null, payload.branding.logo ?? null);
  }
}

/** Memoised manifest source, keyed on the link element. */
const manifestCache = new WeakMap<HTMLLinkElement, Record<string, unknown>>();

function rewriteManifest(
  manifest: HTMLLinkElement,
  appName: string,
  themeColor: string | null,
  logo: string | null,
): void {
  const cached = manifestCache.get(manifest);
  if (cached) {
    patch(manifest, cached, appName, themeColor, logo);
    return;
  }

  const href = manifest.getAttribute("href") ?? "";
  if (!href || href.startsWith("data:")) return;

  void fetch(href)
    .then((res) => res.json())
    .then((base: Record<string, unknown>) => {
      manifestCache.set(manifest, base);
      patch(manifest, base, appName, themeColor, logo);
    })
    .catch(() => {
      // A manifest we cannot read simply stays as the platform default.
    });
}

/**
 * Resolves anything path-like against the page, leaving absolute URLs and
 * protocol-relative ones alone. Returns null for a value that is not a string.
 */
function absoluteUrl(value: unknown): string | null {
  if (typeof value !== "string" || value === "") return null;
  try {
    return new URL(value, document.baseURI).href;
  } catch {
    return null;
  }
}

/**
 * App-icon URLs for this org, or null to keep the platform's own icons.
 *
 * The uploaded logo is a print-quality asset — a 1463×1452 PNG of 3 MB is
 * normal for these orgs. Android's launcher will not take a multi-megabyte
 * icon and silently falls back to a placeholder, so the installed app ends up
 * correctly named and wrongly iconed. The API resizes it instead, letterboxed
 * onto the org's own brand colour, and the manifest points at that.
 *
 * Only an org's own logo is used here. A tenant with no logo of its own keeps
 * the platform icons rather than borrowing the platform's mark as its own.
 */
function appIcons(logo: string | null, themeColor: string | null): Record<string, unknown>[] | null {
  if (!logo || !logo.startsWith("/api/files/")) return null;
  const params = new URLSearchParams({ url: logo });
  if (themeColor) params.set("bg", themeColor);
  const query = params.toString();
  return [192, 512].map((size) => ({
    src: `/api/app-icon/${size}?${query}`,
    sizes: `${size}x${size}`,
    type: "image/png",
    purpose: "any",
  }));
}

function patch(
  manifest: HTMLLinkElement,
  base: Record<string, unknown>,
  appName: string,
  themeColor: string | null,
  logo: string | null,
): void {
  base.name = appName;
  base.short_name = appName.length <= 12 ? appName : appName.split(" ")[0];
  if (themeColor) {
    base.theme_color = themeColor;
    base.background_color = themeColor;
  }

  const icons = appIcons(logo, themeColor);
  if (icons) base.icons = icons;

  /*
   * Absolutise the URL-bearing fields. Written as a Blob (below), so there is
   * no document base for Chrome to resolve these against — a relative
   * `start_url` or icon `src` is silently dropped, and without an icon Chrome
   * will not offer installation at all.
   */
  for (const key of ["start_url", "scope"] as const) {
    const resolved = absoluteUrl(base[key]);
    if (resolved) base[key] = resolved;
  }

  const baseIcons = Array.isArray(base.icons) ? (base.icons as Record<string, unknown>[]) : [];
  for (const icon of baseIcons) {
    const resolved = absoluteUrl(icon.src);
    if (resolved) icon.src = resolved;
  }

  manifest.href = URL.createObjectURL(
    new Blob([JSON.stringify(base)], { type: "application/manifest+json" }),
  );
}